import {useCallback, useState} from "react";
import {imageAttachmentPresignAPI} from "@/app/apiPaths";
import {useFileUpload} from "@/hooks/use-file-upload";
import {useFileDragDrop} from "@/hooks/use-file-drag-drop";
import {useFilePaste} from "@/hooks/use-file-paste";
import {signAttachment} from "@/hooks/attachment-presign";
import {DOCUMENT_VALIDATION, IMAGE_VALIDATION, SPREADSHEET_VALIDATION, isAllowedImageExtension} from "@/lib/validation";
import {
  AttachmentPresignRequest,
  CreateSignedLinkResponse,
} from "@/pb/api/devplan/services/web/company";

export interface AttachmentInfo {
  id: string
  fileName: string
}

export interface UseUnifiedFileUploadOptions {
  companyId: number
  disabled?: boolean
}

export interface UseUnifiedFileUploadReturn {
  /** Current image URLs */
  imageUrls: string[]
  setImageUrls: React.Dispatch<React.SetStateAction<string[]>>
  /** Current document attachments */
  attachments: AttachmentInfo[]
  setAttachments: React.Dispatch<React.SetStateAction<AttachmentInfo[]>>
  /** Whether document attachments are enabled via feature flag */
  documentAttachmentsEnabled: boolean
  /** Total count of files (images + attachments) */
  currentFileCount: number
  /** Whether drag is active */
  dragActive: boolean
  /** Drag event handlers */
  dragHandlers: {
    onDragEnter: (event: React.DragEvent) => void
    onDragLeave: (event: React.DragEvent) => void
    onDragOver: (event: React.DragEvent) => void
    onDrop: (event: React.DragEvent) => Promise<void>
  }
  /** Paste handler for textarea */
  handlePaste: (event: React.ClipboardEvent<HTMLTextAreaElement>) => void
  /** Accepted file types string for input accept attribute */
  acceptedFileTypes: string
  /** Button title based on feature flag */
  buttonTitle: string
  /** Clear all files */
  clearFiles: () => void
  /** Props for FileUpload component */
  fileUploadProps: {
    urlSigner: (extension: string, fileType: string, fileName: string, fileSize: number) => Promise<{ fullPath: string, signedUrl: string }>
    onImageUploaded: (url: string) => void
    onAttachmentUploaded: (attachment: AttachmentInfo) => void
  }
}

/**
 * Hook for unified file uploads with drag/drop and paste support.
 * Use with FileUpload component (companyId mode) for button clicks.
 */
export function useUnifiedFileUpload({
                                       companyId,
                                       disabled = false,
                                     }: UseUnifiedFileUploadOptions): UseUnifiedFileUploadReturn {
  const [imageUrls, setImageUrls] = useState<string[]>([])
  const [attachments, setAttachments] = useState<AttachmentInfo[]>([])
  const allowedMimeTypes = [...IMAGE_VALIDATION.allowedTypes, ...DOCUMENT_VALIDATION.allowedTypes, ...SPREADSHEET_VALIDATION.allowedTypes]
  const allowedExtensions = [...IMAGE_VALIDATION.allowedExtensions, ...DOCUMENT_VALIDATION.allowedExtensions, ...SPREADSHEET_VALIDATION.allowedExtensions]

  // URL signer that routes based on file type
  const urlSigner = useCallback(
    async (extension: string, fileType: string, fileName: string, fileSize: number) => {
      if (fileType.startsWith('image/')) {
        const resp = await fetch(imageAttachmentPresignAPI(companyId), {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(AttachmentPresignRequest.create({
            fileExtension: extension, fileType, fileSize, originalFileName: fileName,
          })),
        })
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({error: 'Image presign failed'}))
          throw new Error(err.error || 'Image presign failed')
        }
        const data = CreateSignedLinkResponse.fromJSON(await resp.json())
        return {fullPath: data.id, signedUrl: data.url}
      } else {
        return signAttachment(companyId, extension, fileType, fileName, fileSize)
      }
    },
    [companyId]
  )

  const {uploadFile} = useFileUpload(
    urlSigner,
    {allowedTypes: [...allowedMimeTypes], allowedExtensions: [...allowedExtensions]},
    {noSuccessToast: true}
  )

  // Callbacks for state updates (used by both drag/drop and FileUpload)
  const onImageUploaded = useCallback((url: string) => {
    setImageUrls(prev => [...prev, url])
  }, [])

  const onAttachmentUploaded = useCallback((attachment: AttachmentInfo) => {
    setAttachments(prev => [...prev, attachment])
  }, [])

  // Upload handler for drag/drop and paste
  const handleFilesUpload = useCallback(async (files: File[]): Promise<string[]> => {
    const allPaths: string[] = []

    for (const file of files) {
      const ext = file.name.split('.').pop()?.toLowerCase() || ''
      const normalizedType = file.type.toLowerCase()
      const hasUnknownType = normalizedType.length === 0 || normalizedType === 'application/octet-stream'
      const isImage = normalizedType.startsWith('image/') || (
        hasUnknownType &&
        isAllowedImageExtension(ext)
      )
      const path = await uploadFile(file)
      if (path) {
        if (isImage) {
          onImageUploaded(path)
        } else {
          onAttachmentUploaded({id: path, fileName: file.name})
        }
        allPaths.push(path)
      }
    }

    return allPaths
  }, [uploadFile, onImageUploaded, onAttachmentUploaded])

  const currentFileCount = imageUrls.length + attachments.length

  const {dragActive, dragHandlers} = useFileDragDrop({
    currentFileCount,
    uploadMultipleFiles: handleFilesUpload,
    onFilesUploaded: () => {
    },
    disabled,
  })

  const handlePaste = useFilePaste({
    currentFileCount,
    uploadMultipleFiles: handleFilesUpload,
    onFilesUploaded: () => {
    },
    disabled,
  })

  const clearFiles = useCallback(() => {
    setImageUrls([])
    setAttachments([])
  }, [])

  const acceptedFileTypes = [
    ...IMAGE_VALIDATION.allowedTypes,
    ...IMAGE_VALIDATION.allowedExtensions.map(ext => `.${ext}`),
    ...DOCUMENT_VALIDATION.allowedTypes,
    ...DOCUMENT_VALIDATION.allowedExtensions.map(ext => `.${ext}`),
    ...SPREADSHEET_VALIDATION.allowedTypes,
    ...SPREADSHEET_VALIDATION.allowedExtensions.map(ext => `.${ext}`),
  ].join(',')

  const buttonTitle = "Attach files (images, PDF, DOCX, Markdown, CSV, XLSX)"

  return {
    imageUrls,
    setImageUrls,
    attachments,
    setAttachments,
    documentAttachmentsEnabled: true,
    currentFileCount,
    dragActive,
    dragHandlers,
    handlePaste,
    acceptedFileTypes,
    buttonTitle,
    clearFiles,
    fileUploadProps: {
      urlSigner,
      onImageUploaded,
      onAttachmentUploaded,
    },
  }
}
